import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Link } from "react-router-dom";
import PokemonSprite from "../components/PokemonSprite";
import TurnCard from "../components/TurnCard";
import SEO from "../components/SEO";

export default function AnalyzeBattlePage() {
  const { replayId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`/api/analyze-battle/${replayId}`)
      .then((res) => setData(res.data))
      .catch(() => {});
  }, [replayId]);

  if (!data) {
    return (
      <Box sx={{ textAlign: "center", mt: 8 }}>
        <SEO
          title="Loading Battle Analysis..."
          description="Loading detailed turn-by-turn analysis for this battle replay."
          keywords="battle analysis, loading"
        />
        <CircularProgress color="secondary" />
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", mt: 2 }}>
          Analyzing battle with AI — this may take a moment
        </Typography>
      </Box>
    );
  }

  const turns = data.analysis.filter((t) => t.turn_number !== 0);

  return (
    <Box component="main" sx={{ p: { xs: 2, md: 4 } }}>
      <SEO
        title={`Analyze Battle ${replayId}`}
        description={`Detailed turn-by-turn analysis of battle replay ${replayId}. Review decisions, team matchups, and key moments from this competitive VGC match.`}
        keywords={`battle analysis, replay ${replayId}, vgc analysis, match review, battle breakdown, turn analysis`}
      />

      {/* ── Page header ── */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
          <Typography component="h1" variant="h4" color="common.white" sx={{ fontWeight: 700 }}>
            Battle Analysis
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.4)", fontFamily: "monospace" }}>
          {data.replayId}
        </Typography>
      </Box>

      {/* ── Beta disclaimer ── */}
      <Typography variant="body2" sx={{ color: "common.white", fontStyle: "italic", mb: 2 }}>
        Currently in beta — results may contain errors or inaccuracies.{" "}
        If you spot something wrong, we'd love to hear about it —{" "}
        <Link to="/contact" style={{ color: "inherit", textDecoration: "underline" }}>let us know</Link>.
      </Typography>

      {/* ── Teams ── */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ mb: 4 }}>
        {["p1", "p2"].map((side, i) => (
          <Box key={side} sx={{ flex: 1 }}>
            <Box>
              <Typography variant="body1" component="p" color="common.white" gutterBottom>
                {side === "p1" ? data.player1 : data.player2}'s Team
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                {data.teams[side].map((p) => (
                  <PokemonSprite key={p.name} pokemon={{ name: p.name }} />
                ))}
              </Stack>
            </Box>
          </Box>
        ))}
      </Stack>

      {/* ── Turn list ── */}
      <Stack spacing={1}>
        {turns.map((turn) => (
          <TurnCard key={turn.turn_number} turn={turn} p1={data.player1} p2={data.player2} />
        ))}
      </Stack>
    </Box>
  );
}
